import { YStack, isWeb, Text, Paragraph } from 'tamagui'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { GuestCard } from '@components/GuestCard'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { router } from 'expo-router'
import { KeyboardAvoidingView, Platform, ActivityIndicator } from 'react-native'
import { useThemeParams } from '@hooks/useThemeParams'
import { LoadingScreen } from '@components/LoadingScreen'
import { useUserPasswordRequestReset } from '@services/api/graphql/mutations/userPasswordRequestReset'
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope'

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const goToLogin = () => {
  if (isWeb) {
    window.location.href = '/users/sign_in'
  } else {
    router.replace('/users/sign_in')
  }
}

export default function PasswordRequest() {
  const { t } = useTranslation('PasswordRequest')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [wasSent, setWasSent] = useState(false)
  const toast = useToastController()
  const { data: themeParams, loading: themeParamsLoading } = useThemeParams()
  const { handleRequestReset } = useUserPasswordRequestReset()

  const school = themeParams?.school

  const isButtonDisabled = useMemo(() => {
    return !email.trim() || !isValidEmail(email) || isSubmitting
  }, [email, isSubmitting])

  const handleSubmit = async () => {
    if (!school) return

    setIsSubmitting(true)
    try {
      await handleRequestReset({ email, schoolId: String(school.id) })
      setWasSent(true)
    } catch {
      toast.show(t('toast.error.title'), {
        message: t('toast.error.message'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (themeParamsLoading) {
    return <LoadingScreen />
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flexGrow: 1 }}
    >
      <GuestCard school={school}>
        <YStack width="100%" alignItems="center" gap="$size.spacing-xs">
          <Paragraph
            color="$text-primary"
            fontSize="$font-size-md"
            fontWeight={700}
            textAlign="center"
            lineHeight={30}
          >
            {wasSent ? t('sent.title') : t('title')}
          </Paragraph>
          <Text
            color="$text-secondary"
            fontSize="$font-size-xs"
            fontFamily="$mulishRegular"
            textAlign="center"
            lineHeight={24}
          >
            {wasSent ? t('sent.description') : t('subtitle')}
          </Text>
        </YStack>

        {wasSent ? (
          <Button
            variant="primary"
            size="large"
            width="100%"
            onPress={goToLogin}
            animation="quick"
            pressStyle={{ scale: 0.97 }}
          >
            {t('sent.cta')}
          </Button>
        ) : (
          <YStack width="100%" gap="$size.spacing-md">
            <Input
              icon={faEnvelope}
              label={t('email.label')}
              placeholder={t('email.placeholder')}
              value={email}
              onChangeText={setEmail}
              onClear={() => setEmail('')}
              autoComplete="email"
              keyboardType="email-address"
              autoCapitalize="none"
            />

            <YStack gap="$size.spacing-xxxs">
              <Button
                variant="primary"
                size="large"
                onPress={handleSubmit}
                disabled={isButtonDisabled}
                animation="quick"
                pressStyle={{ scale: 0.97 }}
                loading={isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="$neutral-0" />
                ) : (
                  t('button.submit')
                )}
              </Button>

              <Button
                variant="secondary"
                size="large"
                onPress={goToLogin}
                animation="quick"
                pressStyle={{ scale: 0.97 }}
              >
                {t('button.back-to-login')}
              </Button>
            </YStack>
          </YStack>
        )}
      </GuestCard>
    </KeyboardAvoidingView>
  )
}
